import type { OptionsFilter, SelectProps } from '@mantine/core'
import { Badge, Button, Card, Group, NativeSelect, Select, Stack, Text } from '@mantine/core'
import { useModals } from '@mantine/modals'
import { ShiftPresenceDot } from '@/components/ui/ShiftPresenceDot'
import { classicPriorityBadgeRootStyle, classicStatusBadgeRootStyle } from '@/lib/classic-ticket-badges'
import { statusLabel } from '@/lib/ticket-display'
import { canAssignTicketAsWorkspaceUser } from '@/lib/workspace-user-roles'
import { TICKET_DETAIL_PRIORITY_OPTIONS } from '@/components/tickets/ticket-detail-page-helpers'
import type { TicketDetailTicket, WorkspaceUserRow } from '@/types/api'

type SelectOption = {
  value: string
  label: string
}

type TicketManagementCardProps = {
  ticket: TicketDetailTicket
  currentUser: { id?: string | null; role?: string | null } | null | undefined
  classicBadgeDark: boolean
  statusOptions: SelectOption[]
  assigneeOptions: SelectOption[]
  assigneeUserById: Map<string, WorkspaceUserRow>
  assigneeFilter: OptionsFilter
  renderAssigneeOption: SelectProps['renderOption']
  onStatus: (status: string) => void
  onPriority: (priority: string) => void
  onAssign: (userId: string | null) => void
  onChangeReplyLocale: (locale: 'en' | 'tr' | null) => void
}

export function TicketManagementCard({
  ticket,
  currentUser,
  classicBadgeDark,
  statusOptions,
  assigneeOptions,
  assigneeUserById,
  assigneeFilter,
  renderAssigneeOption,
  onStatus,
  onPriority,
  onAssign,
  onChangeReplyLocale,
}: TicketManagementCardProps) {
  const { openConfirmModal } = useModals()
  const confirmStatus = (next: string) => {
    if (next === ticket.status) return
    if (next === 'pending') {
      onStatus(next)
      return
    }
    const label = statusLabel(next)
    openConfirmModal({
      title: 'Изменить статус?',
      children: (
        <Text size="sm" c="dimmed">
          Новый статус: {label}
        </Text>
      ),
      labels: { confirm: 'Изменить', cancel: 'Отмена' },
      onConfirm: () => onStatus(next),
    })
  }

  const confirmPriority = (next: string) => {
    if (next === ticket.priority) return
    const label = TICKET_DETAIL_PRIORITY_OPTIONS.find((o) => o.value === next)?.label ?? next
    openConfirmModal({
      title: 'Изменить приоритет?',
      children: (
        <Text size="sm" c="dimmed">
          Новый приоритет: {label}
        </Text>
      ),
      labels: { confirm: 'Изменить', cancel: 'Отмена' },
      onConfirm: () => onPriority(next),
    })
  }

  const confirmAssign = (next: string | null) => {
    const cur = ticket.assigned_to_id ?? null
    if (next === cur) return
    const label =
      next == null
        ? 'Не назначен'
        : assigneeUserById.get(next)
          ? displayUserLabel(assigneeUserById.get(next)!)
          : assigneeOptions.find((o) => o.value === next)?.label || '—'
    openConfirmModal({
      title: 'Изменить назначение?',
      children: (
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      ),
      labels: { confirm: 'Изменить', cancel: 'Отмена' },
      onConfirm: () => onAssign(next),
    })
  }

  const confirmAssignToMe = () => {
    if (!currentUser?.id) return
    if (ticket.assigned_to_id === currentUser.id) return
    openConfirmModal({
      title: 'Назначить на себя?',
      children: (
        <Text size="sm" c="dimmed">
          Обращение будет назначено на вас.
        </Text>
      ),
      labels: { confirm: 'Назначить', cancel: 'Отмена' },
      onConfirm: () => onAssign(currentUser.id ?? null),
    })
  }

  return (
    <Card withBorder padding="xs" radius="md">
      <Text fw={600} size="xs" mb={6}>
        Управление
      </Text>
      <Stack gap="xs">
        <Group gap={6} wrap="nowrap" align="center">
          <Badge
            size="sm"
            variant="light"
            color="gray"
            styles={{
              root: {
                ...classicStatusBadgeRootStyle(ticket.status, classicBadgeDark),
                flexShrink: 0,
                letterSpacing: '0.3px',
                border: 'none',
              },
            }}
          >
            {statusLabel(ticket.status)}
          </Badge>
          <NativeSelect
            id="td-status"
            size="xs"
            aria-label="Статус"
            value={ticket.status}
            style={{ flex: 1, minWidth: 0 }}
            onChange={(e) => confirmStatus(e.currentTarget.value)}
            data={statusOptions}
          />
        </Group>
        <Group gap={6} wrap="nowrap" align="center">
          <Badge
            size="sm"
            variant="light"
            color="gray"
            styles={{
              root: {
                ...classicPriorityBadgeRootStyle(ticket.priority, classicBadgeDark),
                flexShrink: 0,
                letterSpacing: '0.3px',
                border: 'none',
              },
            }}
          >
            {TICKET_DETAIL_PRIORITY_OPTIONS.find((o) => o.value === ticket.priority)?.label ??
              ticket.priority}
          </Badge>
          <NativeSelect
            id="td-priority"
            size="xs"
            aria-label="Приоритет"
            value={ticket.priority}
            style={{ flex: 1, minWidth: 0 }}
            onChange={(e) => confirmPriority(e.currentTarget.value)}
            data={TICKET_DETAIL_PRIORITY_OPTIONS}
          />
        </Group>
        <Group align="flex-end" wrap="nowrap" gap={6}>
          <Select
            id="td-assignee"
            size="xs"
            aria-label="Назначен"
            description="Наблюдателей нельзя назначать исполнителями"
            mb={1}
            style={{ flex: 1, minWidth: 0 }}
            leftSection={ticket.assigned_to_id ? <ShiftPresenceDot userId={ticket.assigned_to_id} size={7} /> : undefined}
            searchable
            clearable={false}
            allowDeselect={false}
            withCheckIcon={false}
            nothingFoundMessage="Никого не найдено"
            maxDropdownHeight={360}
            scrollAreaProps={{
              type: 'auto',
              offsetScrollbars: 'y',
              scrollbarSize: 8,
              styles: {
                root: {
                  maxHeight: 'min(52vh, 380px)',
                },
              },
            }}
            classNames={{ dropdown: 'td-assignee-dropdown' }}
            comboboxProps={{
              position: 'bottom-start',
              offset: 4,
              onOpen: scrollAssigneeDropdownTop,
            }}
            data={assigneeOptions}
            value={ticket.assigned_to_id || ''}
            filter={assigneeFilter}
            renderOption={renderAssigneeOption}
            onChange={(val) => confirmAssign(val === null || val === undefined || val === '' ? null : val)}
          />
          <Button
            size="compact-xs"
            variant="light"
            mb={1}
            style={{ flexShrink: 0 }}
            onClick={confirmAssignToMe}
            disabled={!currentUser?.id || !canAssignTicketAsWorkspaceUser(currentUser?.role)}
          >
            Мне
          </Button>
        </Group>
        {ticket.translation_enabled ? (
          <NativeSelect
            id="td-reply-locale"
            size="xs"
            label="Язык ответов клиенту"
            description={
              ticket.translation_client_locale
                ? `По умолчанию: ${
                    ticket.translation_client_locale === 'tr' ? 'турецкий' : 'английский'
                  } (первое сообщение клиента)`
                : 'Язык определится по первому сообщению клиента'
            }
            value={ticket.translation_reply_locale_override || ''}
            data={[
              { value: '', label: 'Авто' },
              { value: 'en', label: 'Английский' },
              { value: 'tr', label: 'Турецкий' },
            ]}
            onChange={(e) => {
              const v = e.currentTarget.value
              const next = v === '' ? null : (v as 'en' | 'tr')
              if ((next || null) === (ticket.translation_reply_locale_override || null)) return
              onChangeReplyLocale(next)
            }}
          />
        ) : null}
      </Stack>
    </Card>
  )
}

function displayUserLabel(user: WorkspaceUserRow): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  return name || user.email || '—'
}

function scrollAssigneeDropdownTop() {
  const scrollTop = () => {
    const vp = document.querySelector(
      '.td-assignee-dropdown [data-scrollarea-viewport], .td-assignee-dropdown .mantine-ScrollArea-viewport',
    ) as HTMLElement | null
    if (vp) vp.scrollTop = 0
  }
  requestAnimationFrame(() => {
    scrollTop()
    requestAnimationFrame(scrollTop)
  })
}
